<template>
  <div class="actions" aria-live="polite">
    <!-- Header enrichment (checking, then identified): one explicit tap on JOIN, Evina's protected element
         (te = .evina-protect, see functions/evina.js). -->
    <button
      v-if="screen === Steps.Checking || screen === Steps.Ready"
      id="subscribe_button"
      class="btn-join cptpl_subscribe evina-protect"
      data-apex-cta="join"
      type="button"
      :disabled="pending"
      :aria-busy="pending"
      @click="onSubscribe"
    >
      <span v-if="pending" class="spinner" aria-hidden="true"></span>
      <span :class="{ 'visually-hidden': pending }">{{ t.joinCta }}</span>
      <span v-if="screen === Steps.Checking" class="visually-hidden">{{ t.checkingLabel }}</span>
    </button>

    <!-- Not identified (Wi-Fi or off-network): typed number, Telkom texts a PIN. GET PIN carries the
         cptpl_subscribe marker while this step shows, then CONFIRM does (Evina, 14 Oct 2026). It is not
         Evina's click target: no evina-protect here, so te only matches CONFIRM on this flow (Evina, 15 Oct 2026). -->
    <form v-else-if="screen === Steps.Msisdn" class="actions__form" @submit.prevent="onRequestOtp">
      <p class="actions__hint">{{ t.msisdnHint }}</p>
      <label class="field msisdn" :class="{ 'field--error': !!errorMessage, shake: shakeField }" for="msisdn">
        <span class="msisdn__prefix" aria-hidden="true">+{{ brandConfig.msisdn.countryCode }}</span>
        <input
          id="msisdn"
          ref="msisdnInput"
          :value="msisdnLocal"
          class="msisdn__input"
          type="tel"
          inputmode="numeric"
          autocomplete="tel-national"
          :aria-label="t.mobileLabel"
          @input="onMsisdnInput"
        />
      </label>
      <button
        id="subscribe_button"
        class="btn-join btn-join--compact cptpl_subscribe"
        type="submit"
        :disabled="busy || !msisdnLocal"
        :aria-busy="busy"
      >
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        <span :class="{ 'visually-hidden': busy }">{{ t.getPinCta }}</span>
      </button>
    </form>

    <!-- PIN: submit is an explicit tap on CONFIRM (Evina's click target on this flow), never automatic. -->
    <form v-else-if="screen === Steps.Otp" class="actions__form" @submit.prevent="onSubscribe">
      <p class="actions__hint">{{ t.otpHint }}</p>
      <OtpInput v-model="otp" :error="otpError" class="actions__otp" />
      <button
        id="subscribe_button"
        class="btn-join btn-join--compact cptpl_subscribe evina-protect"
        data-apex-cta="join"
        type="submit"
        :disabled="busy || otp.length !== 4 || !evinaReady"
        :aria-busy="busy"
      >
        <span v-if="busy" class="spinner" aria-hidden="true"></span>
        <span :class="{ 'visually-hidden': busy }">{{ t.confirmCta }}</span>
      </button>
    </form>

    <p class="price-line cptpl_price">{{ t.priceLine }}</p>
    <p v-if="errorMessage" class="error-text" role="alert">{{ errorMessage }}</p>

    <div v-if="screen === Steps.Otp" class="actions__links">
      <button class="btn-link" type="button" :disabled="busy" @click="onResend">{{ t.otpResend }}</button>
      <button class="btn-link" type="button" :disabled="busy" @click="store.useAnotherNumber()">{{ t.otpChangeNumber }}</button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore, Steps } from '@/stores/subscriptionStore'
import OtpInput from '@/components/OtpInput.vue'
import { track, sinceNavigation } from '@/functions/telemetry'

const { brandConfig, translations: t } = storeToRefs(useBrandStore())
const store = useSubscriptionStore()
const { step, screen, msisdn, otp, busy, errorCode, headerEnriched, evinaReady } = storeToRefs(store)

const router = useRouter()
const route = useRoute()
const msisdnInput = ref(null)
const shakeField = ref(false)
const otpError = ref(false)

// JOIN waits for the HE answer, then for the Evina kit to be bound (Telkom blocks a tap Evina did not
// see), and while /sub is in flight.
const pending = computed(() => screen.value === Steps.Checking || busy.value || (screen.value === Steps.Ready && !evinaReady.value))

// The page's real "ready" moment for the dashboard: JOIN (or GET PIN) first tappable.
const tappable = computed(() => (screen.value === Steps.Ready && !pending.value) || screen.value === Steps.Msisdn)
let joinReadySent = false
watch(tappable, (value) => {
  if (!value || joinReadySent) return
  joinReadySent = true
  track('join_ready', { ms: sinceNavigation() })
}, { immediate: true })

const msisdnLocal = computed(() => {
  const digits = String(msisdn.value || '').replace(/\D/g, '')
  const code = brandConfig.value.msisdn.countryCode
  return code && digits.startsWith(code) ? digits.slice(code.length) : digits
})

const errorMessage = computed(() => {
  const code = errorCode.value
  if (!code) return ''
  if (code === 'invalidNumber') return t.value.invalidNumber
  if (code === 'NETWORK') return t.value.errorNetwork
  if (code === 'NO_SERVICE') return t.value.errorNoService
  return t.value.errors[code] || t.value.errorGeneric
})

function onMsisdnInput(event) {
  const { countryCode, trunkPrefix, localLength } = brandConfig.value.msisdn
  let local = event.target.value.replace(/\D/g, '')
  if (countryCode && local.startsWith(countryCode) && local.length > localLength) local = local.slice(countryCode.length)
  if (trunkPrefix && local.startsWith(trunkPrefix)) local = local.slice(trunkPrefix.length)
  if (localLength) local = local.slice(0, localLength)
  msisdn.value = local ? `${countryCode}${local}` : ''
  if (event.target.value !== local) event.target.value = local
  if (errorCode.value) errorCode.value = ''
}

function bump(flag) {
  flag.value = false
  requestAnimationFrame(() => (flag.value = true))
  setTimeout(() => (flag.value = false), 500)
}

async function onRequestOtp() {
  const res = await store.requestOtp(brandConfig.value)
  if (res.ok) return
  if (res.fatal) return router.push({ name: 'error', query: route.query })
  bump(shakeField)
  msisdnInput.value?.focus()
}

async function onResend() {
  otp.value = ''
  const res = await store.requestOtp(brandConfig.value)
  if (res.fatal) router.push({ name: 'error', query: route.query })
}

async function onSubscribe() {
  if (pending.value) return
  const res = await store.subscribe(brandConfig.value)
  if (res.ok) return router.push({ name: 'thank-you', query: route.query })
  if (res.fatal) return router.push({ name: 'error', query: route.query })
  if (res.errorCode === 'INVALID_OTP') {
    bump(otpError)
    otp.value = ''
  }
}

watch(step, (value) => {
  if (value === Steps.Msisdn) nextTick(() => msisdnInput.value?.focus())
  if (value === Steps.Done && store.alreadySubscribed) router.push({ name: 'thank-you', query: route.query })
  // Arm Evina on JOIN (HE) and, on the PIN flow, from the number page onward: Evina needs the kit
  // in the page from the MSISDN input, not only from the PIN screen (Evina, 14 Oct 2026). The PIN
  // step arms again so the kit binds to CONFIRM, the protected button.
  if (value === Steps.Ready || value === Steps.Msisdn || value === Steps.Otp) nextTick(() => store.armEvina(brandConfig.value))
})

watch(headerEnriched, (enriched) => enriched && store.checkSubscription(brandConfig.value), { immediate: true })

onMounted(() => {
  // After the pre-lander the visitor is already identified (or still being) and the Evina kit already
  // downloaded: carry on from there instead of starting over, and put the kit in now that this page is
  // what it will see. A /he still in flight reaches the step watch above.
  if (store.resumeAfterPrelander()) {
    if (step.value === Steps.Msisdn) nextTick(() => msisdnInput.value?.focus())
    if (step.value === Steps.Ready || step.value === Steps.Msisdn) nextTick(() => store.armEvina(brandConfig.value))
    return
  }
  store.prefetchEvina(brandConfig.value)
  store.checkHeaderEnrichment(brandConfig.value)
})
</script>

<style scoped>
.actions {
  display: flex;
  flex-direction: column;
}

.actions__form {
  display: flex;
  flex-direction: column;
}

.actions__hint {
  padding-top: 10px;
  font-size: var(--subline-size);
  line-height: 1.5;
  color: var(--subline-color);
}

/* Field and PIN boxes line up with the action under them (both bleed 8px past the gutter). */
.actions__form .field,
.actions__otp {
  width: calc(100% + 16px);
  margin: 14px -8px 0;
}

.actions__form .field + .btn-join,
.actions__otp + .btn-join {
  margin-top: 12px;
}

.actions__links {
  display: flex;
  justify-content: center;
  gap: 18px;
  padding-top: 2px;
}

.msisdn {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: text;
}

.msisdn__prefix {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
}

.msisdn__input {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: transparent;
  color: var(--color-text);
  font-size: 1.2rem;
  letter-spacing: 1.5px;
}

/* One box: the field's border already turns the accent colour on focus (.field:focus-within), so the
   global focus ring on the input inside it would draw a second box. */
.msisdn__input:focus-visible {
  outline: none;
}
</style>
